/**
 * Export helpers for the Insights tab.
 *
 * CSV is generated from the current result rows (client-side, no refetch).
 * PNG is produced from the live ApexCharts instance (see InsightsChart's
 * `exportPng`) and downloaded via `downloadDataUri`.
 *
 * @ticket IB-644
 */

/**
 * Build a CSV string from the current result groups.
 *
 * @param {Object} result - the data-endpoint result currently rendered
 * @returns {string}
 */
export function buildCsv(result) {
    if (!result || !Array.isArray(result.groups)) {
        return '';
    }

    const hasSeries = result.groups.some((g) => Array.isArray(g.series) && g.series.length > 0);

    const header = hasSeries ? ['Group', 'Value', 'Committed', 'Uncommitted'] : ['Group', 'Value'];

    const rows = result.groups.map((group) => {
        const label = csvEscape(group.label);

        if (!hasSeries) {
            return [label, group.value].join(',');
        }

        const committed = (group.series || []).find((s) => s.name === 'Committed');
        const uncommitted = (group.series || []).find((s) => s.name === 'Uncommitted');

        return [label, group.value, committed ? committed.value : '', uncommitted ? uncommitted.value : ''].join(',');
    });

    return [header.join(','), ...rows].join('\n');
}

/**
 * Trigger a browser download of the current result as a CSV file.
 *
 * @param {Object} result
 * @param {string} [filename]
 */
export function downloadCsv(result, filename = 'insights.csv') {
    const csv = buildCsv(result);
    if (!csv) {
        return;
    }

    // Prepend a UTF-8 BOM so Excel on Windows decodes Arabic / non-ASCII labels
    // correctly instead of mojibaking them.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Trigger a browser download of a data URI (used for the chart PNG, whose
 * image data is produced from the live ApexCharts instance by the chart
 * component — see InsightsChart's `exportPng`).
 *
 * @param {string} dataUri
 * @param {string} [filename]
 */
export function downloadDataUri(dataUri, filename = 'insights.png') {
    if (!dataUri) {
        return;
    }

    const link = document.createElement('a');
    link.href = dataUri;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function csvEscape(value) {
    const str = value == null ? '' : String(value);
    if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}
